import type {
  EmbedBlock,
  EmbedContent
} from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import { Node } from "@tiptap/core"
import { ReactNodeViewRenderer } from "@tiptap/react"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import { EmbedExtension } from "../../models"
import { EmbedView } from "./EmbedView"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    embed: {
      setEmbed: () => ReturnType
    }
  }
}

interface Attributes {
  content: { default: EmbedContent | undefined }
}

const component = Node.create({
  name: "embed",

  group: "block",

  atom: true,

  content: "block*",

  // Prevents marks being applied to the content of an embed
  marks: "",

  addAttributes(): Attributes {
    return {
      content: {
        default: undefined
      }
    }
  },

  addCommands() {
    return {
      setEmbed:
        () =>
        ({ tr, dispatch }) => {
          const node = this.type.create()

          if (dispatch) {
            tr.replaceSelectionWith(node)
          }

          return true
        }
    }
  },

  parseHTML() {
    return [
      {
        tag: "embed"
      }
    ]
  },

  renderHTML() {
    return ["embed"]
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedView)
  }
})

const converter = {
  fromPrismic(
    schema: Schema
  ): (embed: EmbedBlock) => ProsemirrorNode | undefined {
    return function (embed: EmbedBlock): ProsemirrorNode | undefined {
      return schema.nodes[component.name]?.create({
        content: embed.data
      })
    }
  },

  toPrismic(node: EmbedNode): EmbedBlock | undefined {
    if (node.attrs.content === undefined) {
      return {
        type: "embed",
        data: {
          type: "embed",
          embed_url: "",
          __TYPE__: "EmbedContent",
          all: []
        }
      }
    }

    return {
      type: "embed",
      data: {
        ...node.attrs.content
      }
    }
  }
}

interface EmbedNode extends ProsemirrorNode {
  attrs: {
    content: EmbedContent | undefined
  }
}

// eslint-disable-next-line import/no-default-export
export default new EmbedExtension(
  [RichTextNodeType.embed],
  component.name,
  component,
  converter
)
